import { useAuth } from "@clerk/clerk-react";
import { Grid, Skeleton } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BentoCategory,
  NicheFollowUpQuestion,
  OrganizationNiche,
  OrganizationNicheStatus,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import StyledCard from "components/Styled/Card";
import QuestionChip from "components/Styled/QuestionChip";
import StyledVaPage from "components/Styled/VAPage";
import { fetcherAuth } from "utils/api";
import { makeDeepCopy } from "utils/updateLocalState";
import { useTracking } from "utils/useTracking";

import styles from "./styles";

interface Props {
  categoriesToPickFrom: BentoCategory[];
  needsFollowUps: boolean;
  selectedCategories: BentoCategory[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<BentoCategory[]>>;
  followupQuestions: NicheFollowUpQuestion[];
}

const SelfServeNichePage = ({
  categoriesToPickFrom,
  selectedCategories,
  setSelectedCategories,
  needsFollowUps,
  followupQuestions,
}: Props) => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { currentOrg, organizationVaProfile, setOrganizationVaProfile } =
    useContext(OrganizationUserContext);
  const { trackVaStep } = useTracking();

  const [loading, setLoading] = useState<boolean>(false);
  const [makeNicheChanges, setMakeNicheChanges] = useState<boolean>(false);

  const selectChip = (chip: BentoCategory) => {
    setMakeNicheChanges(true);
    if (isSelected(chip)) {
      setSelectedCategories((prev) =>
        prev.filter((x: BentoCategory) => x.id !== chip.id),
      );
    } else {
      setSelectedCategories((prev) => [...prev, chip]);
    }
  };

  const generateWidths = (count: number, min = 85, max = 200) => {
    return Array.from(
      { length: count },
      () => `${Math.floor(Math.random() * (max - min + 1)) + min}px`,
    );
  };

  const loadingElements = useMemo(
    () => generateWidths(23),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const isSelected = (chip: BentoCategory) => {
    return selectedCategories?.some((x: BentoCategory) => x.id === chip.id);
  };

  const submitNiches = async () => {
    if (selectedCategories.length === 0) {
      setAlert("Please select at least one niche to continue.", "warning");
      return;
    }

    if (!makeNicheChanges) {
      goNext(needsFollowUps);
      return;
    }

    setLoading(true);
    try {
      const { niches } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/organization-niches/from-onboarding`,
        "POST",
        {},
        {
          bentoCategoryIds: selectedCategories.map((cat) => cat.id),
        },
      );
      setOrganizationVaProfile((prev) => {
        const copy = makeDeepCopy(prev) || {};
        copy.niches = niches;
        return copy;
      });
      const localNeedsFollowUp =
        niches?.length > 0
          ? followupQuestions?.some((question) =>
              niches
                .map((niche: OrganizationNiche) => niche.bentoCategoryId)
                .includes(question.triggerBentoCategoryId),
            )
          : false;

      goNext(localNeedsFollowUp);
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
      navigate(`/${routes.nicheSelfServeOnboarding}`);
    } finally {
      setLoading(false);
    }
  };

  const goNext = (needsFollowUps: boolean) => {
    if (needsFollowUps) {
      trackVaStep(routes.nichesFollowUpSelfServeOnboarding);
      navigate(`/${routes.nichesFollowUpSelfServeOnboarding}`);
    } else {
      trackVaStep(routes.templatesSelfServeOnboarding);
      navigate(`/${routes.templatesSelfServeOnboarding}`);
    }
  };

  useEffect(() => {
    if (
      organizationVaProfile?.niches &&
      organizationVaProfile?.niches?.length > 0 &&
      categoriesToPickFrom?.length > 0 &&
      selectedCategories?.length === 0
    ) {
      const selectedNiches: BentoCategory[] = [];
      organizationVaProfile.niches.forEach((niche: OrganizationNiche) => {
        if (
          (!niche?.isArchived &&
            niche?.status === OrganizationNicheStatus.active) ||
          niche?.status === OrganizationNicheStatus.pending
        ) {
          const matchedCategory = categoriesToPickFrom.find(
            (category) => category.id === niche.bentoCategoryId,
          );
          if (matchedCategory) {
            selectedNiches.push(matchedCategory);
          }
        }
      });
      setSelectedCategories(selectedNiches);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizationVaProfile?.niches?.length,
    categoriesToPickFrom?.length,
    selectedCategories?.length,
  ]);

  return (
    <StyledVaPage
      stepNumber={2}
      title="Let's Set Up Your Niches 📦"
      description="We recommend selecting multiple niches to boost your chances of landing
        more deals."
      body={
        <StyledCard styles={{ px: 2, py: 4, mb: 4 }}>
          <Grid container justifyContent="center" gap={2}>
            {(categoriesToPickFrom?.length === 0 || loading) &&
              loadingElements.map((width, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  width={width}
                  sx={styles.loadingSkeleton}
                />
              ))}
            {!loading &&
              categoriesToPickFrom?.map((category, index) => (
                <QuestionChip
                  key={`niche-${index}`}
                  isSelected={isSelected(category)}
                  label={category.name}
                  onClick={() => selectChip(category)}
                />
              ))}
          </Grid>
        </StyledCard>
      }
      buttonNext={
        needsFollowUps
          ? "Continue to Additional Questions"
          : "Continue to Templates"
      }
      onNext={submitNiches}
      buttonBack="Go Back to About"
      onBack={() => {
        trackVaStep(routes.aboutSelfServeOnboarding);
        navigate(`/${routes.aboutSelfServeOnboarding}`);
      }}
      loading={loading}
    />
  );
};

export default SelfServeNichePage;
